import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getBlogPosts } from '../../services/CommonServices';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import IntlObject from '../Common/IntlObject';
import Constants from 'constants/Constants';
import LoginStore from 'stores/LoginStore';
import EditableObjectHandle from '../Common/EditableObjectHandler';

const Section = styled.section`
  position: relative;
  padding: 50px 0;
  background-color: ${props => props.backgroundColor || '#fafafa'};
  width: 100%;

  @media (max-width: 1400px) {
    padding: 30px 20px;
  }
`;

const Container = styled.div`
  @media (min-width: 1401px) {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 15px;
  }

  @media (max-width: 1400px) {
    max-width: 100%;
    padding: 0;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 30px;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  letter-spacing: 2px;
`;

const ArticleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ArticleCard = styled.article`
  background: #fff;
  border: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const ArticleImageLink = styled(Link)`
  display: block;
  width: 100%;
  height: 140px;
  background-color: #f0f0f0;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  border-bottom: 1px solid #e8e8e8;
  cursor: pointer;

  @media (max-width: 600px) {
    height: 160px;
  }
`;

const ArticleContent = styled.div`
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  flex: 1;

  @media (max-width: 600px) {
    padding: 12px 14px;
  }
`;

const ArticleMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const CategoryTag = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  color: #c41e3a;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DateText = styled.span`
  font-size: 0.65rem;
  color: #888;
`;

const ArticleTitleLink = styled(Link)`
  font-size: 0.9rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: #c41e3a;
  }

  @media (max-width: 600px) {
    font-size: 0.95rem;
    -webkit-line-clamp: 3;
  }
`;

const ArticleExcerpt = styled.p`
  font-size: 0.75rem;
  color: #555;
  line-height: 1.5;
  margin: 0 0 12px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;

  @media (max-width: 600px) {
    font-size: 0.8rem;
    -webkit-line-clamp: 4;
  }
`;

const ReadMoreLink = styled(Link)`
  font-size: 0.7rem;
  font-weight: 600;
  color: #1a1a1a;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: auto;

  &:hover {
    color: #c41e3a;
  }

  &::after {
    content: '→';
    font-size: 0.8rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 50px 20px;
  color: #888;
  font-size: 0.85rem;
`;

const ViewAllLink = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 30px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #1a1a1a;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1px;

  &:hover {
    color: #c41e3a;
  }
`;

// Helper function to extract first image from HTML content
const extractFirstImage = (htmlContent) => {
  if (!htmlContent) return null;
  const imgMatch = htmlContent.match(/<img[^>]+src="([^">]+)"/);
  return imgMatch ? imgMatch[1] : null;
};

// Helper function to strip HTML and get plain text excerpt
const getExcerpt = (htmlContent, maxLength = 120) => {
  if (!htmlContent) return '';
  const plainText = htmlContent.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
  if (plainText.length <= maxLength) return plainText;
  return plainText.substring(0, maxLength).trim() + '...';
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const BlogPreviewSection = ({ siteId, id, options = {} }) => {
  const [posts, setPosts] = useState([]);
  const [authInfo, setAuthInfo] = useState(LoginStore.authInfo);

  // Default objects for editing
  const defaultObjects = {
    fullWidth: 'true',
    backgroundColor: '#f9f9f9',
  };

  // Options with defaults
  const backgroundColor = options.backgroundColor || '#f9f9f9';

  // Auth listener
  useEffect(() => {
    const onAuthChange = () => setAuthInfo(LoginStore.authInfo);
    LoginStore.addChangeListener(onAuthChange);
    return () => LoginStore.removeChangeListener(onAuthChange);
  }, []);

  const isLogin = authInfo ? authInfo.status === Constants.AUTHED : false;

  useEffect(() => {
    if (siteId) {
      getBlogPosts(siteId, { limit: 6 })
        .then(res => {
          if (res.data.success) {
            setPosts(res.data.data.posts.filter(p => p.is_published));
          }
        })
        .catch(err => console.error(err));
    }
  }, [siteId]);

  if (posts.length === 0) {
    return (
      <Section backgroundColor={backgroundColor}>
        <EditableObjectHandle
          id={id}
          objects={options || defaultObjects}
          defaultObjects={defaultObjects}
          isAdmin={isLogin}
          componentTitle="BlogPreviewSection"
        />
        <Container>
          <SectionTitle>
            <IntlObject keycode="blog.latest_posts" returnType="span" />
          </SectionTitle>
          <EmptyState>
            <FormattedMessage id="blog.no_posts" defaultMessage="No posts available yet." />
          </EmptyState>
        </Container>
      </Section>
    );
  }

  return (
    <Section backgroundColor={backgroundColor}>
      <EditableObjectHandle
        id={id}
        objects={options || defaultObjects}
        defaultObjects={defaultObjects}
        isAdmin={isLogin}
        componentTitle="BlogPreviewSection"
      />
      <Container>
        <SectionTitle>
          <IntlObject keycode="blog.latest_posts" returnType="span" />
        </SectionTitle>

        <ArticleGrid>
          {posts.map((post) => {
            const imageUrl = post.thumbnail || post.featured_image || extractFirstImage(post.content);
            const excerpt = getExcerpt(post.content);

            return (
              <ArticleCard key={post.id}>
                {imageUrl && (
                  <ArticleImageLink to={`/blog/${post.slug}`} src={imageUrl} />
                )}
                <ArticleContent>
                  <ArticleMeta>
                    {post.category && (
                      <CategoryTag>{post.category}</CategoryTag>
                    )}
                    <DateText>{formatDate(post.created_at)}</DateText>
                  </ArticleMeta>
                  <ArticleTitleLink to={`/blog/${post.slug}`}>{post.title}</ArticleTitleLink>
                  <ArticleExcerpt>{excerpt}</ArticleExcerpt>
                  <ReadMoreLink to={`/blog/${post.slug}`}>
                    <FormattedMessage id="blog.read_more" defaultMessage="Read more" />
                  </ReadMoreLink>
                </ArticleContent>
              </ArticleCard>
            );
          })}
        </ArticleGrid>

        <ViewAllLink to="/blog">
          <FormattedMessage id="blog.view_all" defaultMessage="View All Articles" /> →
        </ViewAllLink>
      </Container>
    </Section>
  );
};

export default BlogPreviewSection;
